import * as React from "react";
import { useAmbassadorProgramEnabled } from "@/hooks/useAmbassadorProgramEnabled";
import { COOKIE_SETTINGS_EVENT } from "@/lib/cookie-consent";
import { openAiTesterSession } from "@/lib/openaiLiveTester";
import { Link } from "@tanstack/react-router";

import { ChevronDown, Instagram } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";
import { BrandWordmark } from "@/components/BrandWordmark";
import { JOURNAL_LINKS_ENABLED } from "@/lib/feature-flags";
import { PaymentBadges } from "@/components/product/PaymentBadges";

export function Footer() {
  const ambassadorProgramOn = useAmbassadorProgramEnabled();
  const { t, dir, locale } = useLocale();
  const [expanded, setExpanded] = React.useState(false);
  const [isConsentTester, setIsConsentTester] = React.useState(false);
  React.useEffect(() => {
    setIsConsentTester(!!openAiTesterSession());
  }, []);
  const footerRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (e: PointerEvent) => {
      if (footerRef.current && !footerRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [expanded]);

  return (
    <footer
      ref={footerRef}
      data-site-footer
      className="border-t border-foreground bg-background text-foreground"
    >
      {/* Grid — Nemro 7-col: signup(2) + 4 menus + about(1) */}
      <div dir="ltr" className="mx-auto grid w-full max-w-7xl grid-cols-1 md:grid-cols-7">
        {/* Newsletter signup — 2 cols (المصدر الوحيد للنشرة: الفوتر) */}
        <div
          dir={dir}
          className="block border-b border-foreground/20 px-6 py-8 md:col-span-2 md:min-h-[280px] md:border-b-0 md:border-e md:border-foreground/20 md:px-8 md:py-10"
        >
          <h2 className="mb-6 font-serif text-[22px] font-normal leading-tight md:mb-7 md:text-[26px]">
            {t("section.newsletter")}
          </h2>
          <p className="mb-6 max-w-sm text-[12px] leading-relaxed text-foreground/70 md:mb-8">
            {t("footer.tagline")}
          </p>
          {ambassadorProgramOn && (
          <Link
            to="/ambassadors"
            className="inline-flex w-full max-w-[340px] items-center justify-between border border-foreground px-4 py-3.5 text-[12px] uppercase tracking-[0.14em] transition-colors hover:bg-foreground hover:text-background"
          >
            <span>{t("ambassadors.cta.start_lover_button")}</span>
            <span aria-hidden="true">→</span>
          </Link>
          )}
        </div>

        {/* Columns wrapper — hidden on mobile unless expanded */}
        <div dir={dir} className={`md:contents ${expanded ? "contents" : "hidden"}`}>
          <FooterCol title={t("footer.shop")}>
            <FooterLink to="/shop">{t("nav.shop")}</FooterLink>
            {/* الهدايا معطّلة مؤقّتاً */}
          </FooterCol>

          <FooterCol title={t("footer.discover")}>
            {ambassadorProgramOn && <FooterLink to="/ambassadors">{t("footer.ambassador")}</FooterLink>}
            {JOURNAL_LINKS_ENABLED && <FooterLink to="/journal">{t("nav.journal")}</FooterLink>}
            <li>
              <span className="block py-2 text-[13px] leading-relaxed text-foreground/60 md:py-0 md:text-[12.5px] lg:whitespace-nowrap lg:text-sm">
                {t("nav.about")}
              </span>
            </li>
          </FooterCol>

          <FooterCol title={t("footer.help")}>
            <FooterLink to="/faq">{t("footer.faq")}</FooterLink>
            <FooterLink to="/contact">{t("nav.contact")}</FooterLink>
            <FooterExternal href={`https://wa.me/${LECMO_WA_NUMBER}`}>
              {t("footer.whatsapp")}
            </FooterExternal>
            <span className="block py-2 text-[13px] leading-relaxed text-foreground/60 md:py-0 md:text-[12.5px] lg:whitespace-nowrap lg:text-sm">
              {t("footer.partners")}
            </span>
          </FooterCol>

          <FooterCol title={t("footer.account_col")}>
            <FooterLink to="/account">{t("footer.my_account")}</FooterLink>
            <FooterLink to="/track-order">{t("nav.track_order")}</FooterLink>
            <FooterLink to="/account/orders">{t("footer.my_orders")}</FooterLink>
            <FooterLink to="/account/wishlist">{t("footer.wishlist")}</FooterLink>
          </FooterCol>

          <FooterCol title={t("footer.legal")} last>
            <FooterLink to="/shipping">{t("footer.shipping")}</FooterLink>
            <FooterLink to="/returns">{t("footer.returns")}</FooterLink>
            <FooterLink to="/privacy">{t("footer.privacy")}</FooterLink>
            <FooterLink to="/terms">{t("footer.terms")}</FooterLink>
            <FooterLink to="/cookies">{t("footer.cookies")}</FooterLink>
            <FooterLink to="/accessibility">{t("footer.accessibility")}</FooterLink>
            <FooterLink to="/copyright">{t("footer.copyright_page")}</FooterLink>
          </FooterCol>
        </div>
      </div>

      {isConsentTester && (
        <div className="px-4 py-2 text-center">
          <button
            type="button"
            aria-controls="lecmo-cookie-choices"
            className="min-h-10 text-xs underline underline-offset-4 focus-visible:outline focus-visible:outline-2"
            onClick={() => window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))}
          >
            {locale === "ar"
              ? "إعدادات ملفات تعريف الارتباط"
              : locale === "fr"
                ? "Paramètres des cookies"
                : "Cookie settings"}
          </button>
        </div>
      )}
      {/* Mobile-only toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="mt-0 flex w-full items-center justify-center gap-2 border-y border-foreground/20 py-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground/80 md:hidden"
      >
        {expanded ? t("footer.less") : t("footer.more")}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Brand mark strip — Nemro signature row */}
      <div className="mx-auto flex w-full max-w-7xl items-center justify-center border-t border-foreground/20 py-5 md:py-14">
        <BrandWordmark className="h-[26px] w-auto opacity-90 md:h-[56px]" />
      </div>

      {/* طرق الدفع المفعّلة */}
      <div className="mx-auto w-full max-w-7xl border-t border-foreground/20 px-6 py-4 md:px-8">
        <PaymentBadges />
      </div>

      {/* Bottom row — social + copyright + admin */}
      <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-2 border-t border-foreground/20 px-6 py-3 text-[10.5px] text-foreground/70 md:flex-row md:items-center md:gap-4 md:px-8 md:py-5 md:text-[11px]">
        <div className="flex flex-wrap items-center gap-5">
          <a
            href="https://www.instagram.com/lecmo.ae/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 transition-colors hover:text-foreground"
          >
            <Instagram className="h-3.5 w-3.5" />
            @lecmo.ae
          </a>
          <span className="opacity-60">
            {t("footer.copyright_line", { year: String(new Date().getFullYear()) })}
          </span>
          <span className="opacity-60">{t("footer.trn")}</span>
        </div>
        <Link
          to="/auth"
          search={{ redirect: "/admin/gifts", mode: "signup" }}
          className="inline-flex min-h-[36px] items-center text-[10px] uppercase tracking-[var(--tracking-wider)] text-foreground/65 transition-colors hover:text-foreground"
        >
          {t("footer.admin_login")}
        </Link>
      </div>
    </footer>
  );
}

/**
 * Nemro-style footer column. Border-based dividers between columns on desktop.
 * On mobile behaves as an accordion (retained from previous behaviour).
 */
function FooterCol({
  title,
  children,
  last,
}: {
  title: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className={`border-b border-foreground/20 px-6 py-4 md:min-h-[280px] md:border-b-0 md:px-6 md:py-10 ${
        last ? "" : "md:border-e md:border-foreground/20"
      }`}
    >
      {/* Mobile: collapsible header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-2.5 md:hidden"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground">
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {/* Desktop: static heading */}
      <p className="mb-6 hidden text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground md:block">
        {title}
      </p>
      <ul
        className={`overflow-hidden transition-all duration-300 md:!max-h-none md:!opacity-100 md:!pb-0 md:flex md:flex-col md:gap-4 ${
          open ? "max-h-96 pb-3 opacity-100" : "max-h-0 pb-0 opacity-0"
        }`}
      >
        {children}
      </ul>
    </div>
  );
}

type FooterLinkTo =
  | "/shop"
  | "/gifts"
  | "/journal"
  | "/about"
  | "/contact"
  | "/shipping"
  | "/returns"
  | "/privacy"
  | "/terms"
  | "/cookies"
  | "/accessibility"
  | "/copyright"
  | "/faq"
  | "/account"
  | "/account/orders"
  | "/track-order"
  | "/account/wishlist"
  | "/account/ambassador"
  | "/ambassadors";

function FooterLink({ to, children }: { to: FooterLinkTo; children: React.ReactNode }) {
  return (
    <li>
      <Link
        to={to}
        className="block py-2 text-[13px] leading-relaxed text-foreground/70 transition-colors duration-200 hover:text-foreground md:py-0 md:text-[12.5px] lg:whitespace-nowrap lg:text-sm"
      >
        {children}
      </Link>
    </li>
  );
}

function FooterExternal({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="block py-2 text-[13px] leading-relaxed text-foreground/70 transition-colors duration-200 hover:text-foreground md:py-0 md:text-[12.5px] lg:whitespace-nowrap lg:text-sm"
      >
        {children}
      </a>
    </li>
  );
}
